import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header';
import { Footer } from '../Footer';
import styles from './SolutionPages.module.css';
import { BarChart3, Building2, ShoppingCart, CheckCircle2, ArrowRight } from 'lucide-react';
import solution11 from './solution images/solution 1.1.png';
import solution12 from './solution images/solution 1.2.png';
import solution13 from './solution images/solution 1.3.png';
import solution14 from './solution images/solution 1.4.png';
import solution33 from './solution images/solution 3.3.png';
import solution34 from './solution images/solution 3.4.png';
import solution21 from './solution images/solution 2.1.png';
import solution22 from './solution images/solution 2.2.png';
import solution23 from './solution images/solution 2.3.png';
import solution24 from './solution images/solution 2.4.png';
import solution31 from './solution images/solution 3.1.png';
import solution32 from './solution images/solution 3.2.png';

const industryData = {
    'saas-technology': {
        title: 'SaaS and Technology',
        icon: <BarChart3 size={ 48 } />,
        color: '#3b82f6',
        headline: 'Conversation Intelligence Built For Fast-Moving SaaS & Technology Sales Teams',
        subheadline: 'Automatically record demos, summarize discovery calls, track objections, and keep your CRM perfectly updated — without manual notes.',
        challenge: {
            title: 'The Challenge for Tech Sales Teams',
            description: 'SaaS sales moves fast — but critical insights get lost:',
            points: [
                'Discovery calls aren\'t reviewed',
                'Objections aren\'t tracked across deals',
                'Follow-ups depend on rep memory',
                'CRM notes are incomplete or late',
                'Managers lack visibility into real conversations'
            ],
            result: 'Slower deal cycles, lower revenue, missed targets and unpredictable forecasts.'
        },
        howItHelps: {
            title: 'How Galile Helps SaaS Teams Win More Deals',
            sections: [
                {
                    title: 'Capture every customer interaction',
                    description: 'Automatically record:',
                    points: ['Discovery calls', 'Product demos', 'Pricing discussions', 'Renewal conversations'],
                    footer: 'Across Zoom, Google Meet, phone calls, and in-person meetings.'
                },
                {
                    title: 'Turn calls into structured deal intelligence',
                    description: 'Galile\'s AI extracts:',
                    points: ['Pain points', 'Budget & authority signals', 'Objections and competitors', 'Buying intent', 'Next steps'],
                    footer: 'So every deal has real conversation data, not just CRM guesses.'
                },
                {
                    title: 'Keep your CRM clean — automatically',
                    points: ['Summaries pushed to CRM', 'Action items created', 'Deal notes structured', 'Follow-ups never missed'],
                    footer: 'Your CRM becomes a true system of record, without rep effort.'
                },
                {
                    title: 'Coach reps using real conversations',
                    description: 'Managers can:',
                    points: ['Review key moments instantly', 'Compare talk ratios across reps', 'Identify winning discovery patterns', 'Onboard new reps faster']
                }
            ]
        },
        outcomes: {
            title: 'Outcomes SaaS/Tech Teams See in Weeks',
            points: [
                'Faster deal cycles',
                'Higher demo-to-close rates',
                'Cleaner CRM data',
                'Better onboarding for new AEs',
                'More predictable pipeline forecasting'
            ]
        },
        whoFor: {
            title: 'Who This Is For',
            roles: ['Founder-led SaaS sales', 'SDR + AE teams', 'Inside sales teams', 'Customer success & renewals teams']
        },
        ctaText: 'Stop losing insights from your sales calls. See how Galile turns conversations into revenue intelligence.'
    },
    'bfsi-insurance': {
        title: 'BFSI, Loan, and Insurance',
        icon: <Building2 size={ 48 } />,
        color: '#059669',
        headline: 'AI Conversation Intelligence For High-Volume BFSI Sales',
        subheadline: 'Capture every customer call, ensure compliance, track intent, and improve conversion across loan, insurance, and financial product sales.',
        challenge: {
            title: 'The Reality of BFSI Sales',
            description: 'Financial sales teams handle:',
            points: [
                'Hundreds of daily calls',
                'Complex compliance requirements',
                'Multiple follow-ups before conversion',
                'Distributed field + call center teams'
            ],
            additionalText: 'Yet most conversations are:',
            additionalPoints: ['Untracked', 'Unreviewed', 'Poorly documented', 'Impossible to analyze at scale'],
            result: 'This leads to lost conversions, compliance risk, and weak coaching visibility.'
        },
        howItHelps: {
            title: 'How Galile Transforms BFSI Sales Conversations',
            sections: [
                {
                    title: 'Record and organize every interaction',
                    description: 'Capture:',
                    points: ['Loan qualification calls', 'Insurance advisory conversations', 'Verification calls', 'Renewal reminders', 'Field sales discussions'],
                    footer: 'Across phone, online meetings, and in-person conversations.'
                },
                {
                    title: 'Detect intent, objections, and drop-off points',
                    description: 'Galile\'s AI highlights:',
                    points: ['Customer hesitation signals', 'Pricing objections', 'Documentation blockers', 'Competitor comparisons', 'Readiness to convert'],
                    footer: 'So teams know which leads to prioritize.'
                },
                {
                    title: 'Strengthen compliance and audit readiness',
                    points: ['Call summaries stored securely', 'Consent-aware recording flows', 'Easy retrieval for audits', 'Standardized documentation across reps'],
                    footer: 'Reduce regulatory and operational risk.'
                },
                {
                    title: 'Improve coaching and conversion rates',
                    description: 'Managers can:',
                    points: ['Review real customer conversations', 'Identify winning scripts', 'Detect mis-selling risks', 'Train new agents faster']
                }
            ]
        },
        outcomes: {
            title: 'Results BFSI Teams Achieve',
            points: [
                'Higher lead-to-conversion rates',
                'Faster turnaround on follow-ups',
                'Reduced compliance exposure',
                'Better agent productivity',
                'Clear visibility into customer intent'
            ]
        },
        whoFor: {
            title: 'Ideal For',
            roles: ['Loan sales teams', 'Insurance advisors', 'NBFC sales operations', 'Banking outbound teams', 'Financial field agents']
        },
        ctaText: 'Bring intelligence and compliance to every sales call.'
    },
    'fmcg': {
        title: 'FMCG',
        icon: <ShoppingCart size={ 48 } />,
        color: '#d97706',
        headline: 'Conversation Intelligence Built For High-Velocity FMCG Sales',
        subheadline: 'Capture distributor and retailer conversations, track field interactions, and turn daily sales discussions into structured insights and follow-ups.',
        challenge: {
            title: 'The Challenge in FMCG Sales',
            description: 'FMCG sales teams operate in fast, distributed, field-heavy environments:',
            points: [
                'Daily retailer visits',
                'Distributor negotiations',
                'Scheme and pricing discussions',
                'Order follow-ups via phone or WhatsApp'
            ],
            additionalText: 'But most of these conversations:',
            additionalPoints: ['Are never recorded', 'Depend on rep memory', 'Reach managers too late', 'Never enter the CRM accurately'],
            result: 'This leads to: Missed orders and follow-ups, Poor visibility into market feedback, Weak territory forecasting, Inconsistent execution across reps.'
        },
        howItHelps: {
            title: 'How Galile Powers Smarter FMCG Sales Conversations',
            sections: [
                {
                    title: 'Capture distributor and retailer discussions',
                    description: 'Reps can:',
                    points: ['Record in-store conversations', 'Upload voice notes after visits', 'Capture phone follow-ups with distributors', 'Generate instant AI summaries'],
                    footer: 'All without slowing down daily field activity.'
                },
                {
                    title: 'Turn market conversations into structured intelligence',
                    description: 'Galile extracts:',
                    points: ['Order intent and quantity discussions', 'Pricing or scheme objections', 'Competitor presence at store level', 'Stock availability concerns', 'Next visit or follow-up'],
                    footer: 'Giving managers real-time visibility into ground reality.'
                },
                {
                    title: 'Improve territory visibility and execution',
                    description: 'Leaders can:',
                    points: ['Track visit outcomes across regions', 'Identify stalled distributors early', 'Compare rep activity and effectiveness', 'Monitor scheme communication consistency'],
                    footer: 'This turns field conversations into measurable sales data.'
                },
                {
                    title: 'Strengthen accountability without extra admin',
                    points: ['Automatic visit summaries', 'CRM updates without typing', 'Clear follow-up tracking', 'Transparent deal/order progress'],
                    footer: 'Reps stay focused on selling, not reporting.'
                }
            ]
        },
        outcomes: {
            title: 'Business Impact for FMCG Organizations',
            points: [
                'Higher order conversion from visits',
                'Faster response to retailer feedback',
                'Better distributor engagement',
                'Improved territory forecasting',
                'Stronger execution consistency across teams'
            ]
        },
        whoFor: {
            title: 'Best Fit For',
            roles: ['FMCG field sales representatives', 'Distributor management teams', 'Territory sales managers', 'Regional sales heads', 'Channel sales operations']
        },
        ctaText: 'Bring real-time intelligence to every distributor and retailer conversation.'
    }
};

const SolutionIndustries = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const data = industryData[slug];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!data) {
        return (
            <div className={ styles.container }>
                <Header />
                <div className={ styles.notFound }>
                    <h1>Industry Solution Not Found</h1>
                    <button onClick={ () => navigate('/') }>Return Home</button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={ styles.container }>
            <Header />

            <main className={ styles.main }>
                {/* Hero Section */ }
                <section className={ styles.hero } style={ { '--accent-color': data.color } }>
                    <div className={ styles.heroContent }>
                        <div className={ styles.iconBox }>{ data.icon }</div>
                        <h1 className={ styles.headline }>{ data.headline }</h1>
                        <p className={ styles.subheadline }>{ data.subheadline }</p>
                        <a href="https://calendly.com/viraj-galile/30min" target="_blank" rel="noopener noreferrer" style={ { textDecoration: 'none' } }>
                            <button className={ styles.ctaButton }>
                                Book a Demo
                            </button>
                        </a>
                    </div>
                </section>

                {/* Challenge Section */ }
                <section className={ styles.section }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.challenge.title }</h2>
                        <p className={ styles.description }>{ data.challenge.description }</p>
                        
                        <div className={ styles.problemContainer }>
                            <div className={ styles.problemGrid }>
                                { data.challenge.points && (
                                    <div className={ styles.problemCard }>
                                        <div className={ styles.problemCardHeader }>
                                            <div className={ styles.problemCardIcon }>📋</div>
                                            <h3 className={ styles.problemCardTitle }>Key Challenges</h3>
                                        </div>
                                        <ul className={ styles.bulletList }>
                                            { data.challenge.points.map((point, i) => (
                                                <li key={ i }>{ point }</li>
                                            )) }
                                        </ul>
                                    </div>
                                ) }
                                
                                { data.challenge.additionalPoints && (
                                    <div className={ styles.problemCard }>
                                        <div className={ styles.problemCardHeader }>
                                            <div className={ styles.problemCardIcon }>⚠️</div>
                                            <h3 className={ styles.problemCardTitle }>{ data.challenge.additionalText || 'Impact' }</h3>
                                        </div>
                                        <ul className={ styles.bulletList }>
                                            { data.challenge.additionalPoints.map((point, i) => (
                                                <li key={ i }>{ point }</li>
                                            )) }
                                        </ul>
                                    </div>
                                ) }
                            </div>
                            
                            <div className={ styles.resultBox }>
                                <div className={ styles.resultIcon }>💡</div>
                                <p className={ styles.resultText }>{ data.challenge.result }</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Helps Section - Zigzag Layout */ }
                <section className={ styles.sectionAlt }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.howItHelps.title }</h2>
                        <div className={ styles.zigzagContainer }>
                            { data.howItHelps.sections.map((section, i) => (
                                <div key={ i } className={ `${styles.zigzagRow} ${i % 2 === 1 ? styles.zigzagRowReverse : ''}` }>
                                    <div className={ styles.zigzagContent }>
                                        <h3 className={ styles.zigzagTitle }>{ section.title }</h3>
                                        { section.description && <p className={ styles.zigzagDesc }>{ section.description }</p> }
                                        <ul className={ styles.zigzagList }>
                                            { section.points.map((point, j) => (
                                                <li key={ j }>
                                                    <CheckCircle2 size={ 18 } className={ styles.checkIcon } />
                                                    <span>{ point }</span>
                                                </li>
                                            )) }
                                        </ul>
                                        { section.footer && <p className={ styles.zigzagFooter }>{ section.footer }</p> }
                                    </div>
                                    <div className={ styles.zigzagImage }>
                                        <div className={ styles.imagePlaceholder }>
                                            { slug === 'saas-technology' && i === 0 ? (
                                                <img src={ solution11 } alt="Solution visualization" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' } } />
                                            ) : slug === 'saas-technology' && i === 1 ? (
                                                <img src={ solution12 } alt="Solution visualization" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' } } />
                                            ) : slug === 'saas-technology' && i === 2 ? (
                                                <img src={ solution13 } alt="Solution visualization" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' } } />
                                            ) : slug === 'saas-technology' && i === 3 ? (
                                                <img src={ solution14 } alt="Solution visualization" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' } } />
                                            ) : slug === 'bfsi-insurance' && i === 0 ? (
                                                <img src={ solution21 } alt="Recording and Organization Dashboard" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'bfsi-insurance' && i === 1 ? (
                                                <img src={ solution22 } alt="Intent Detection UI" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'bfsi-insurance' && i === 2 ? (
                                                <img src={ solution23 } alt="Compliance Security Logs" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'bfsi-insurance' && i === 3 ? (
                                                <img src={ solution24 } alt="Coaching Performance UI" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'fmcg' && i === 0 ? (
                                                <img src={ solution31 } alt="Field Sales Tracker" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'fmcg' && i === 1 ? (
                                                <img src={ solution32 } alt="Market Intelligence UI" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'fmcg' && i === 2 ? (
                                                <img src={ solution33 } alt="Territory Performance Map" style={ { width: '100%', height: '100%', objectFit: 'contain', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : slug === 'fmcg' && i === 3 ? (
                                                <img src={ solution34 } alt="Automated Visit Summaries" style={ { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e5e7eb' } } />
                                            ) : (
                                                <div className={ styles.placeholderIcon } style={ { background: '#f3f4f6', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', border: '1px solid #e5e7eb' } }>
                                                    { i === 0 && <BarChart3 size={ 48 } /> }
                                                    { i === 1 && <CheckCircle2 size={ 48 } /> }
                                                    { i === 2 && <Building2 size={ 48 } /> }
                                                    { i === 3 && <ShoppingCart size={ 48 } /> }
                                                </div>
                                            ) }
                                        </div>
                                    </div>
                                </div>
                            )) }
                        </div>
                    </div>
                </section>

                {/* Outcomes Section */ }
                <section className={ styles.section }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.outcomes.title }</h2>
                        <div className={ styles.outcomesGrid }>
                            { data.outcomes.points.map((point, i) => (
                                <div key={ i } className={ styles.outcomeCard }>
                                    <CheckCircle2 size={ 24 } className={ styles.outcomeIcon } />
                                    <span>{ point }</span>
                                </div>
                            )) }
                        </div>
                    </div>
                </section>

                {/* Who For Section */ }
                <section className={ styles.sectionAlt }>
                    <div className={ styles.contentWrapper }>
                        <h2 className={ styles.sectionTitle }>{ data.whoFor.title }</h2>
                        <div className={ styles.rolesList }>
                            { data.whoFor.roles.map((role, i) => (
                                <div key={ i } className={ styles.roleItem }>
                                    <span className={ styles.roleBullet }>•</span>
                                    <span>{ role }</span>
                                </div>
                            )) }
                        </div>
                    </div>
                </section>

                {/* CTA Section */ }
                <section className={ styles.ctaSection }>
                    <div className={ styles.ctaCard }>
                        <h2>{ data.ctaText }</h2>
                        <a href="https://calendly.com/viraj-galile/30min" target="_blank" rel="noopener noreferrer" style={ { textDecoration: 'none' } }>
                            <button className={ styles.ctaButtonLarge }>
                                Book a Demo <ArrowRight size={ 20 } />
                            </button>
                        </a>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default SolutionIndustries;
